import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { IChecklistItem } from '../../../models/common';
import { memoize } from '../../../utilities/memoize';
import { BaseFormControlComponent } from '../base-form-control.component';

/**
 * @group Forms
 * @component Checklist
 *
 * @description Generates a list of checkbox items from an array.
 * @returns An array of values reflecting which items are checked.
 * @export
 * @class LsqChecklistComponent
 * @extends BaseFormControlComponent
 * @property {Array<string|number|ChecklistItem>} items An array of items to add to the checklist.
 * @property {string} [parentClass] Adds to the `class` on the list element.
 */
@Component({
    selector: 'lsq-checklist',
    template: `
        <ul class="lsq-checklist-list list-unstyled {{ parentClass }}">
            <li *ngFor="let item of items; let i = index" class="lsq-checklist-item">
                <lsq-checkbox
                    [id]="createId(item.label, i)"
                    [ngModel]="indexCheckedMap.get(i)"
                    (ngModelChange)="updateModel($event, i)"
                    [label]="item.label"
                    [disabled]="item.disabled"
                    [partiallyChecked]="item.partiallyChecked"
                >
                </lsq-checkbox>
            </li>
        </ul>
    `,
    styles: [
        `
            .lsq-checklist-item {
                margin-bottom: 9px;
            }
        `
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LsqChecklistComponent),
            multi: true
        }
    ]
})
export class LsqChecklistComponent extends BaseFormControlComponent<any[]> implements OnInit {
    @Input() public items: IChecklistItem[] = [];
    @Input() public parentClass?: string;

    public indexCheckedMap = new Map<number, boolean>();
    public readonly color = Color;
    public readonly icons = Icons;

    public ngOnInit(): void {
        this.items.forEach((item, index) =>
            this.indexCheckedMap.set(index, this.getControl()?.value?.includes(item.value))
        );
    }

    @memoize()
    public createId(label: string, index: number, suffix: string = ''): string {
        label = label.replace(/\s/g, '-').toLowerCase();
        return label + (suffix ? '-' + suffix : '') + '--' + index.toString();
    }

    public updateModel(checked: boolean, index: number): void {
        this.indexCheckedMap.set(index, checked);
        this.handleChange(
            this.items.filter((_, i) => this.indexCheckedMap.get(i)).map(({ value }) => value)
        );
    }
}
